import React, { useCallback, useMemo, useState } from 'react';
import { useApiDelete, useApiGet } from 'hooks/useApi';
import Pagination from 'models/Pagination';
import { useNavigate } from 'react-router-dom';
import useUser from 'hooks/useUser';
import TypeResource from 'enum/TypeResource';
import TypeAction from 'enum/TypeAction';
import apiPaths from 'constants/apiPaths';
import { useToast } from 'hooks/useToast';
import ToastType from 'enum/ToastType';
import { ExternalNews } from 'models/ExternalNews';

const useControllerExternalNewsListPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [filteredData, setFilteredData] = React.useState({
    pageSize: 10,
    page: 1,
    search: ''
  });
  const { data: internalsNews, isLoading, refetch } = useApiGet<
    Pagination<ExternalNews>
  >(apiPaths.externalNews, {
    params: filteredData
  });

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [internalsNewsIdToDelete, setinternalsNewsIdToDelete] = useState<
    string | null
  >(null);

  const { mutate: mutateDelete } = useApiDelete(
    `${apiPaths.internalNews}/${internalsNewsIdToDelete}`
  );

  const handleSearch = useCallback(
    (search: string) => {
      setFilteredData({ ...filteredData, search });
    },
    [filteredData]
  );
  const handlePageChange = useCallback(
    (page: number) => {
      setFilteredData({ ...filteredData, page });
    },
    [filteredData]
  );
  const goToEdit = useCallback(
    (row: { id: string }) => {
      navigate(`/news/externals/${row.id}`);
    },
    [navigate]
  );
  const user = useUser();
  const userCanAdd = useMemo(() => {
    return user?.resources?.[TypeResource.NEWS]?.includes(TypeAction.CREATE);
  }, [user]);

  const userCanDelete = useMemo(() => {
    return user?.resources?.[TypeResource.COMPANIES]?.includes(
      TypeAction.DELETE
    );
  }, [user]);

  const handleClearFilter = useCallback(() => {
    setFilteredData({ pageSize: 10, page: 1, search: '' });
  }, []);

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const toggleMenu = (id: string) => {
    setOpenDropdownId((prevId) => (prevId === id ? null : id));
  };

  const handleDeleteClick = useCallback((id: string) => {
    setinternalsNewsIdToDelete(id);
    setShowDeleteConfirmation(true);
  }, []);

  const handleOnDelete = useCallback(() => {
    if (!internalsNewsIdToDelete || !userCanDelete) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao excluir notícia'
        );
      },
      onSuccess: () => {
        setShowDeleteConfirmation(false);
        setinternalsNewsIdToDelete(null);
        addToast(ToastType.SUCCESS, 'Notícia excluído com sucesso');
        refetch();
      }
    });
  }, [addToast, mutateDelete, userCanDelete, internalsNewsIdToDelete, refetch]);

  return {
    internalsNews,
    isLoading,
    handleSearch,
    handlePageChange,
    goToEdit,
    userCanAdd,
    openDropdownId,
    userCanDelete,
    toggleMenu,
    setinternalsNewsIdToDelete,
    showDeleteConfirmation,
    handleClearFilter,
    handleDeleteClick,
    handleOnDelete,
    setShowDeleteConfirmation
  };
};

export default useControllerExternalNewsListPage;
